import { useState, useId } from "react";
import styled, { css } from "styled-components";
import { ChevronDown } from "lucide-react";

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Item = styled.div`
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  background: linear-gradient(
      180deg,
      hsl(${({ theme }) => theme.colors.card}) 0%,
      hsl(${({ theme }) => theme.colors.card}) 50%,
      hsl(${({ theme }) => theme.colors.muted} / 0.35) 100%
    );
  box-shadow: ${({ theme }) => theme.shadow.cardMd};
  overflow: hidden;
  transition: border-color 0.22s ease, box-shadow 0.22s ease;

  &[data-open="true"] {
    border-color: hsl(${({ theme }) => theme.colors.primary} / 0.42);
    box-shadow:
      ${({ theme }) => theme.shadow.card},
      0 0 0 1px hsl(${({ theme }) => theme.colors.primary} / 0.08);
  }

  &:focus-within {
    border-color: hsl(${({ theme }) => theme.colors.primary} / 0.35);
  }
`;

const triggerIconStyles = css`
  flex-shrink: 0;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: ${({ theme }) => theme.radius.md};
  background: hsl(${({ theme }) => theme.colors.primary} / 0.1);
  color: hsl(${({ theme }) => theme.colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  text-align: left;
  padding: 1rem 1.125rem;
  cursor: pointer;
  border: none;
  background: transparent;
  color: hsl(${({ theme }) => theme.colors.foreground});

  &::before {
    content: "?";
    ${triggerIconStyles};
    font-size: 0.8125rem;
    font-weight: 800;
    line-height: 1;
    font-family: ui-sans-serif, system-ui, sans-serif;
  }

  &:hover::before {
    background: hsl(${({ theme }) => theme.colors.primary} / 0.16);
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px hsl(${({ theme }) => theme.colors.primary} / 0.45);
    border-radius: ${({ theme }) => theme.radius.md};
  }
`;

/** Icon column next to pseudo “?” — swap to Chevron only on the right */
const TriggerInner = styled.span`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
`;

const QuestionText = styled.span`
  font-size: clamp(0.975rem, 0.92rem + 0.22vw, 1.0825rem);
  font-weight: 700;
  line-height: 1.38;
  padding-top: 0.125rem;
`;

const ChevWrap = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radius.full};
  background: hsl(${({ theme }) => theme.colors.muted});
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease,
    color 0.2s ease;

  ${Trigger}:hover & {
    background: hsl(${({ theme }) => theme.colors.primary} / 0.12);
    color: hsl(${({ theme }) => theme.colors.primary});
  }

  ${({ $open }) =>
    $open &&
    css`
      background: hsl(${({ theme }) => theme.colors.primary} / 0.14);
      color: hsl(${({ theme }) => theme.colors.primary});
    `}

  ${({ $rotated }) =>
    $rotated &&
    css`
      transform: rotate(180deg);
    `}
`;

const Panel = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PanelInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const Answer = styled.div`
  padding: 0 1.125rem 1.125rem 3.875rem;
  font-size: clamp(0.9rem, 0.84rem + 0.18vw, 1rem);
  line-height: 1.72;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});

  p {
    margin: 0 0 0.75rem;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0.5rem 0 0.75rem;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.35rem;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
    color: hsl(${({ theme }) => theme.colors.foreground});
  }

  a {
    color: hsl(${({ theme }) => theme.colors.primary});
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    padding-left: 1.125rem;
  }
`;

/** Strip leading `? ` from pseudo if we hide left column on mobile - we keep padding responsive */

/** Render FAQ answer (plain text or HTML from admin/seed). */
function answerHtml(text) {
  const s = String(text || "").trim();
  if (!s) return { __html: "" };
  if (/<[a-z][\s\S]*>/i.test(s)) return { __html: s };
  return { __html: s.replace(/\n/g, "<br />") };
}

/**
 * Accessible FAQ accordion (single-expanded item).
 *
 * @param {{ items: { q: string, a: string }[] }} props
 */
export default function FaqAccordion({ items = [] }) {
  const uid = useId();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <List role="region" aria-label="Frequently asked questions">
      {items.map((item, index) => {
        const panelId = `${uid}-panel-${index}`;
        const isOpen = openIndex === index;

        return (
          <Item key={`${uid}-item-${index}`} data-open={isOpen ? "true" : "false"}>
            <h2 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
              <Trigger
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                id={`${uid}-trigger-${index}`}
                onClick={() => toggle(index)}
              >
                <TriggerInner>
                  <QuestionText>{item.q}</QuestionText>
                  <ChevWrap $rotated={isOpen} $open={isOpen} aria-hidden>
                    <ChevronDown size={18} strokeWidth={2.25} />
                  </ChevWrap>
                </TriggerInner>
              </Trigger>
            </h2>
            <Panel id={panelId} $open={isOpen} aria-labelledby={`${uid}-trigger-${index}`}>
              <PanelInner>
                <Answer dangerouslySetInnerHTML={answerHtml(item.a)} />
              </PanelInner>
            </Panel>
          </Item>
        );
      })}
    </List>
  );
}
